import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ReferralContextType {
  referralCode: string | null;
  setReferralCode: (code: string | null) => void;
  trackReferral: (email: string) => Promise<void>;
}

const ReferralContext = createContext<ReferralContextType | undefined>(undefined);

export const ReferralProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [referralCode, setReferralCodeState] = useState<string | null>(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const refParam = params.get('ref');
      if (refParam) {
        setReferralCodeState(refParam);
        localStorage.setItem('referralCode', refParam);
      } else {
        const storedCode = localStorage.getItem('referralCode');
        if (storedCode) setReferralCodeState(storedCode);
      }
    } catch { /* storage unavailable */ }
  }, []);

  const setReferralCode = (code: string | null) => {
    setReferralCodeState(code);
    try {
      if (code) localStorage.setItem('referralCode', code);
      else localStorage.removeItem('referralCode');
    } catch { /* storage unavailable */ }
  };

  const trackReferral = async (email: string) => {
    if (!referralCode) {
      if (import.meta.env.DEV) console.log('No referral code to track');
      return;
    }

    try {
      if (import.meta.env.DEV) console.log('Tracking referral');
      const { data, error } = await supabase.functions.invoke('track-referral', {
        body: { referralCode, email }
      });

      if (error) throw error;

      if (data.success) {
        if (import.meta.env.DEV) console.log('Referral tracked successfully');
        // Clear the referral code after successful tracking
        setReferralCode(null);
      } else {
        if (import.meta.env.DEV) console.log('Referral not tracked:', data.message);
      }
    } catch (error) {
      console.error('Error tracking referral:', error);
    }
  };

  return (
    <ReferralContext.Provider value={{ referralCode, setReferralCode, trackReferral }}>
      {children}
    </ReferralContext.Provider>
  );
};

export const useReferral = () => {
  const context = useContext(ReferralContext);
  if (context === undefined) {
    throw new Error('useReferral must be used within a ReferralProvider');
  }
  return context;
};
